import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { StethoscopeIcon, UserIcon, HeartPulseIcon } from 'lucide-react';
import { type Role } from '../data/surveyData';
interface RoleSelectorProps {
  onSelect: (role: Role) => void;
}
const roleCards: {
  role: Role;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}[] = [{
  role: 'Nephrologist',
  title: 'Nephrologist',
  subtitle: 'Primary audience',
  icon: <StethoscopeIcon className="w-6 h-6" data-id="element-126" />,
  color: 'text-era-red',
  bgColor: 'bg-era-red-light'
}, {
  role: 'GP',
  title: 'General Practitioner',
  subtitle: 'Primary care perspective',
  icon: <UserIcon className="w-6 h-6" data-id="element-127" />,
  color: 'text-era-teal',
  bgColor: 'bg-era-teal-light'
}, {
  role: 'Nurse',
  title: 'Renal Nurse / Specialist Nurse',
  subtitle: 'Nursing perspective',
  icon: <HeartPulseIcon className="w-6 h-6" data-id="element-128" />,
  color: 'text-era-blue',
  bgColor: 'bg-era-blue-light'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut'
    }
  }
};
export function RoleSelector({
  onSelect
}: RoleSelectorProps) {
  return <div className="min-h-screen bg-white flex flex-col items-center justify-center px-5 py-10" data-id="element-129">
      <motion.div className="w-full max-w-sm" variants={containerVariants} initial="hidden" animate="visible" data-id="element-130">
        <motion.div variants={cardVariants} className="text-center mb-8" data-id="element-131">
          <div className="flex items-center justify-center gap-5 mb-4" data-id="element-132">
            <img src="https://cdn.magicpatterns.com/uploads/ij2eNBe1HzJZKgaPMQ9K3H/Screenshot_2026-03-31_at_15.45.48.png" alt="ERA - Leading European Nephrology" className="h-12 object-contain" data-id="element-133" />
            <div className="w-px h-8 bg-gray-200" data-id="element-134" />
            <img src="https://cdn.magicpatterns.com/uploads/ezbVhRkDuKPWYLVJz9KwsF/Screenshot_2026-03-31_at_16.01.46.png" alt="CSL" className="h-8 object-contain" data-id="element-135" />
          </div>

          <h1 className="text-2xl font-bold text-clinical-text mb-2" data-id="element-136">
            Select your role
          </h1>
          <p className="text-sm text-clinical-secondary" data-id="element-137">
            See how your perspective compares within your field.
          </p>
        </motion.div>

        <div className="grid gap-3" data-id="element-138">
          {roleCards.map(card => <motion.button key={card.role} variants={cardVariants} whileTap={{
          scale: 0.97
        }} onClick={() => onSelect(card.role)} className="w-full bg-white border-2 border-gray-100 rounded-2xl p-4 flex items-center gap-4 text-left active:border-era-red transition-all duration-150" data-id="element-139">
              <div className={`flex-shrink-0 w-12 h-12 rounded-xl ${card.bgColor} ${card.color} flex items-center justify-center`} data-id="element-140">
                {card.icon}
              </div>
              <div data-id="element-141">
                <div className="text-sm font-bold text-clinical-text" data-id="element-142">
                  {card.title}
                </div>
                <div className="text-xs text-clinical-muted mt-0.5" data-id="element-143">
                  {card.subtitle}
                </div>
              </div>
            </motion.button>)}
        </div>
      </motion.div>
    </div>;
}